import React from 'react';
import { MapPinIcon, MonitorIcon, ExternalLinkIcon, PlayCircleIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { VENUE_TYPES, VIRTUAL_PLATFORMS } from '../../data/eventSchema';
import type { FullEventData } from '../../data/eventSchema';
interface LocationBlockProps {
  event: FullEventData;
}
export const LocationBlock: React.FC<LocationBlockProps> = ({
  event
}) => {
  const dk = useDarkClasses();
  if (event.isVirtual && event.virtualDetails) {
    const platformLabel = VIRTUAL_PLATFORMS.find(p => p.value === event.virtualDetails?.platform)?.label || event.virtualDetails.platform;
    return <div className={`p-6 rounded-xl shadow-sm border mb-6 ${dk.card} ${dk.border}`} data-id="element-9765" data-mp="3noa8k31i3f90">
        <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-9766" data-mp="f8gtztqqcdul">
          <MonitorIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-9767" data-mp="jyx5v9ewjreu" />
          Virtual Event Location
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-9768" data-mp="3taus714izg0q">
          <div data-id="element-9769" data-mp="3pa13i5j9dx6s">
            <p className={`font-medium text-lg ${dk.textPrimary}`} data-id="element-9770" data-mp="3pakawsba936k">
              {platformLabel}
            </p>
            {event.virtualDetails.replayAvailable && <div className={`flex items-center mt-2 text-sm ${dk.textSecondary}`} data-id="element-9771" data-mp="251qiahh59lj">
                <PlayCircleIcon className="w-4 h-4 mr-1 text-green-500" data-id="element-9772" data-mp="gtfzz49ebwb2" />
                Replay will be available after the event
              </div>}
          </div>

          {(event.status === 'upcoming' || event.status === 'tickets-live') && event.virtualDetails.accessLink && <a href={event.virtualDetails.accessLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors" data-id="element-9773" data-mp="kvb22az5idpw">
                Join Event <ExternalLinkIcon className="w-4 h-4 ml-2" data-id="element-9774" data-mp="qlvvjs07i5mc" />
              </a>}
        </div>
      </div>;
  }
  if (!event.isVirtual && event.location) {
    const venueTypeLabel = VENUE_TYPES.find(v => v.value === event.location?.venueType)?.label || event.location.venueType;
    return <div className={`p-6 rounded-xl shadow-sm border mb-6 ${dk.card} ${dk.border}`} data-id="element-9775" data-mp="19ina5o4szwk2">
        <div className="flex justify-between items-start mb-4" data-id="element-9776" data-mp="1ehqf78ksa2s1">
          <h2 className={`text-lg font-bold flex items-center ${dk.textHeading}`} data-id="element-9777" data-mp="1efvu9u9iiany">
            <MapPinIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-9778" data-mp="1jajkpyo2tf56" />
            Location
          </h2>
          {venueTypeLabel && <span className={`px-2 py-1 text-xs font-medium rounded-md ${dk.surfaceMuted} ${dk.textSecondary}`} data-id="element-9779" data-mp="1s9erpcyy2t9j">
              {venueTypeLabel}
            </span>}
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start gap-4" data-id="element-9780" data-mp="3hqinkvrwk6ac">
          <div data-id="element-9781" data-mp="3cqnxgufybkqc">
            <p className={`font-bold text-lg mb-1 ${dk.textPrimary}`} data-id="element-9782" data-mp="3cqw80folqdyd">
              {event.location.venueName}
            </p>
            <p className={dk.textSecondary} data-id="element-9783" data-mp="38q3ydlbti71k">{event.location.streetAddress}</p>
            <p className={dk.textSecondary} data-id="element-9784" data-mp="33riyxo4e3re6">
              {event.location.city}, {event.location.region}{' '}
              {event.location.postcode}
            </p>
            <p className={dk.textSecondary} data-id="element-9785" data-mp="a0u7pjwz0hz">{event.location.country}</p>

            {event.location.roomOrTable && <p className={`mt-2 font-medium ${dk.textPrimary}`} data-id="element-9786" data-mp="3rnmzm2s1aldf">
                Room/Table: {event.location.roomOrTable}
              </p>}
          </div>

          {event.location.mapLink && <a href={event.location.mapLink} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${dk.border} ${dk.textPrimary} hover:${dk.surfaceMuted}`} data-id="element-9787" data-mp="o0ekbaixup9x">
              View Map <ExternalLinkIcon className="w-4 h-4 ml-2" data-id="element-9788" data-mp="7ti7ld9updzr" />
            </a>}
        </div>
      </div>;
  }
  return null;
};